import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { toast, ToastOptions } from 'react-toastify';

import ToastError from '~/shared/assets/icons/circled-cancel-filled.svg';
import ToastInfo from '~/shared/assets/icons/info.svg';
import ToastSuccess from '~/shared/assets/icons/circled-check-filled.svg';
import ToastWarning from '~/shared/assets/icons/exclamation-fill.svg';

import Button from '../components/ButtonsV2';
import Typography from '../components/Typography';

type Variant = 'info' | 'warning' | 'error' | 'success';
type VariantStyleItem = {
  Icon: typeof ToastInfo;
  bg: string;
  text: string;
  border: string;
  fill: string;
};
// eslint-disable-next-line react-refresh/only-export-components
function Toast({
  title,
  message,
  variant,
  volumeId,
  closeToast,
}: {
  title: string;
  message: string;
  variant: Variant;
  volumeId?: string;
  closeToast?: () => void;
}) {
  const { bg, Icon, text, border, fill } = variantsStylesMap[variant];
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (volumeId) {
      navigate(`/fleet-manager/${volumeId}`);
      if (closeToast) {
        closeToast();
      }
    }
  };

  return (
    <div
      className={clsx(
        'flex gap-4 justify-between w-full p-[12px] pr-0 border-l-[6px] rounded-tl-[12px] rounded-bl-[12px] ',
        border
      )}
    >
      <div className="flex gap-[30px] items-center w-full">
        <div
          className={clsx(
            'w-[75px] aspect-square rounded-[18.75px] flex items-center justify-center',
            bg
          )}
        >
          <Icon className={clsx('w-[50px] aspect-square', fill)} />
        </div>

        <div className="flex flex-col py-[7px] gap-[8px]">
          <Typography variant="H3-med" color={text} fontWeight={500} className="font-family-outfit">
            {title}
          </Typography>
          <div className="max-w-[702px]">
            <Typography
              variant="toast"
              color="text-gray-2"
              fontWeight={400}
              className="font-family-inter"
            >
              {message}
            </Typography>
          </div>
        </div>
      </div>
      {volumeId && (
        <div className="  h-full gap-2">
          <Button
            variant="third"
            size="sm"
            onClick={handleNavigate}
            className="whitespace-nowrap  "
          >
            <Typography variant="buttons-m" fontWeight={600} className="font-family-inter">
              View volume
            </Typography>
          </Button>
        </div>
      )}
    </div>
  );
}

type ToastProps = {
  title: string;
  message: string;
  variant: Variant;
  volumeId?: string;
};

export const toaster = ({
  title,
  message,
  variant,
  volumeId,
  ...rest
}: ToastProps & ToastOptions) =>
  toast(({ closeToast }) => <Toast {...{ title, message, variant, volumeId, closeToast }} />, {
    ...rest,
  });

const variantsStylesMap: {
  [k in Variant]: VariantStyleItem;
} = {
  info: {
    Icon: ToastInfo,
    bg: 'bg-primary-100',
    text: 'text-primary-400',
    border: 'border-primary-100',
    fill: 'fill-primary-400',
  },
  error: {
    Icon: ToastError,
    bg: 'bg-light-red',
    text: 'text-red',
    border: 'border-light-red',
    fill: 'fill-red',
  },
  warning: {
    Icon: ToastWarning,
    bg: 'bg-light-orange',
    text: 'text-orange',
    border: 'border-light-orange',
    fill: 'fill-orange',
  },
  success: {
    Icon: ToastSuccess,
    bg: 'bg-light-green',
    text: 'text-green',
    border: 'border-light-green',
    fill: 'fill-green',
  },
};
